import { useMemo } from 'react';
import { normalizeRole } from '../../utils/assignmentRules';
import { extractId } from '../../utils/queryDisplayUtils';

/**
 * Hook to calculate query permissions based on user and query
 * @param {Object} user - Current user from auth context
 * @param {Object} query - Query object
 * @param {Array} sortedThread - Sorted thread messages
 * @returns {Object} Permission flags
 */
export const useQueryPermissions = (user, query, sortedThread) => {
  const permissions = useMemo(() => {
    if (!user || !query) {
      return {
        hasRespondAccess: false,
        canRespond: false,
        canUpdateStatus: false,
        isOriginator: false,
        isThreadDisabledForOriginator: false,
        isEscalatedViewOnly: false,
        canSeeStatusSection: false,
        normalizedUserRole: '',
        normalizedCurrentStatus: '',
        isSubmittedStatus: false,
      };
    }

    // Extract IDs
    const currentUserId = extractId(user?.id || user?._id);
    const assigneeId = extractId(query?.assignee);
    const ownerId = extractId(query?.raisedBy);
    const assignorId = extractId(query?.assignor);
    const escalatedToId = extractId(query?.escalatedTo);
    const isEscalated = Boolean(query?.isEscalated);

    // Check if current user is the originator (person who raised the query)
    const isOriginator = Boolean(
      currentUserId &&
      ownerId &&
      currentUserId.toString() === ownerId.toString()
    );

    // Check if assignee has responded to the query
    const hasAssigneeResponded = sortedThread && sortedThread.length > 0 && assigneeId
      ? sortedThread.some((message) => {
          if (!message || !message.sentBy) return false;
          const messageSenderId = extractId(message.sentBy);
          return messageSenderId && messageSenderId.toString() === assigneeId.toString();
        })
      : false;

    // If originator hasn't received response from assignee yet, disable conversation thread
    const isThreadDisabledForOriginator = isOriginator && !hasAssigneeResponded;

    // Determine if escalated view only
    const isEscalatedViewOnly =
      Boolean(isEscalated) &&
      Boolean(escalatedToId) &&
      Boolean(currentUserId) &&
      escalatedToId === currentUserId;

    // Determine if the current user can respond:
    // assignee, creator (raisedBy), or assignor.
    const hasRespondAccess = Boolean(
      currentUserId &&
      (
        (assigneeId && assigneeId.toString() === currentUserId.toString()) ||
        (ownerId && ownerId.toString() === currentUserId.toString()) ||
        (assignorId && assignorId.toString() === currentUserId.toString())
      )
    );

    const normalizedUserRole = normalizeRole(user?.role);
    const canSeeStatusSection = normalizedUserRole !== 'ADMIN' && normalizedUserRole !== 'CH';
    
    const normalizedCurrentStatus = (query.statusLabel || query.status || '')
      .toString()
      .trim()
      .toLowerCase();
    const isSubmittedStatus = normalizedCurrentStatus === 'submitted';
    const isClosed = Boolean(query?.isClosed) || normalizedCurrentStatus === 'closed';

    const canRespond =
      hasRespondAccess &&
      !isSubmittedStatus &&
      !isEscalatedViewOnly &&
      !isThreadDisabledForOriginator &&
      !isClosed;
    const canUpdateStatus = canSeeStatusSection && hasRespondAccess && !isEscalatedViewOnly && !isClosed;

    return {
      hasRespondAccess,
      canRespond,
      canUpdateStatus,
      isOriginator,
      isThreadDisabledForOriginator,
      isEscalatedViewOnly,
      canSeeStatusSection,
      normalizedUserRole,
      normalizedCurrentStatus,
      isSubmittedStatus,
      isEscalated,
      isClosed,
    };
  }, [user, query, sortedThread]);

  return permissions;
};
